import { FC, ReactNode, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

// ── SidebarSection ────────────────────────────────────────────────────────

interface SidebarSectionProps {
  label: string;
  icon: ReactNode;
  children: ReactNode;
  empty: boolean;
}

export const SidebarSection: FC<SidebarSectionProps> = ({
  label,
  icon,
  children,
  empty,
}) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <Box>
      <ButtonBase
        onClick={() => setExpanded((v) => !v)}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          px: 2,
          py: 1,
          bgcolor: "grey.100",
          gap: 1,
          justifyContent: "flex-start",
        }}
      >
        <Box sx={{ color: "text.secondary", display: "flex", fontSize: 18 }}>
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.8,
            flex: 1,
            textAlign: "left",
          }}
        >
          {label}
        </Typography>
        {expanded ? (
          <ExpandLessIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        ) : (
          <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        )}
      </ButtonBase>
      <Collapse in={expanded}>
        {empty ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", px: 2, py: 1, fontStyle: "italic" }}
          >
            None added
          </Typography>
        ) : (
          children
        )}
      </Collapse>
    </Box>
  );
};

// ── CriterionSidebarItem ──────────────────────────────────────────────────

interface CriterionSidebarItemProps {
  label: string;
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  wasGenerated?: boolean;
  weight?: number;
}

export const CriterionSidebarItem: FC<CriterionSidebarItemProps> = ({
  label,
  selected,
  onSelect,
  onDelete,
  wasGenerated,
  weight,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      bgcolor: selected ? "action.selected" : "transparent",
      "&:hover": { bgcolor: selected ? "action.selected" : "action.hover" },
      borderLeft: "3px solid",
      borderColor: selected ? "primary.main" : "transparent",
    }}
  >
    <ButtonBase
      onClick={onSelect}
      sx={{
        flex: 1,
        px: 2,
        py: 1,
        justifyContent: "flex-start",
        textAlign: "left",
        overflow: "hidden",
        gap: 0.75,
      }}
    >
      {wasGenerated && (
        <AutoFixHighIcon
          sx={{ fontSize: 16, color: "#1DA5C3", flexShrink: 0 }}
        />
      )}
      <Typography
        variant="body2"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
        }}
      >
        {label}
      </Typography>
      {weight !== undefined && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ flexShrink: 0 }}
        >
          {weight}
        </Typography>
      )}
    </ButtonBase>
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onDelete();
      }}
      aria-label="Remove criterion"
      sx={{ mr: 0.5, flexShrink: 0 }}
    >
      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Box>
);
